import { WebViewerInstance } from '@pdftron/webviewer';
import type { VirtualElement } from '@popperjs/core';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { usePopper } from 'react-popper';
import { useDispatch } from 'react-redux';

import {
  Portal,
  Box,
  useTheme,
  IconButtonGroup,
  Flex,
} from '@frontend/components';

import {
  CopyAction,
  LearnMoreAction,
  useCopyAction,
  FindingContent,
  FindingFooterActions,
} from 'modules/common/components/findings';
import { learnMoreUrl } from 'modules/quotes/quoteItems/components/PartExpression/partbotLearnMore';
import {
  useGeometricActions,
  GeometricActionsSet,
} from 'modules/viewerPage/apps/quotePartViewerApps/partSetup/components/findingsActions/geometricActions';
import {
  useMarkInaccurateAction,
  MarkInaccurateAction,
} from 'modules/viewerPage/apps/quotePartViewerApps/partSetup/components/findingsActions/markInaccurateActions';
import {
  usePartDescriptorActions,
  PartDescriptorActionsSet,
} from 'modules/viewerPage/apps/quotePartViewerApps/partSetup/components/findingsActions/partDescriptorActions';
import {
  useCreateRuleAction,
  CreateRuleIconAction,
} from 'modules/viewerPage/apps/quotePartViewerApps/reqsReview/components/findingsActions';

import type { ISuggestionAnnotation } from '../annotationCommands/SuggestionAnnotation';
import { SUGGESTION_ANNOTATION_COMMAND_TYPES } from '../annotationCommands/consts';
import { releaseDocumentFocus } from '../annotationCommands/suggestionUtils';
import {
  ANNOTATION_METADATA_TYPES,
  type FoundInFileMetadata,
} from '../annotationCommands/types';
import { suggestionAnnotations } from '../data/actions';
import { useSuggestionAnnotationCommandConsumer } from '../hooks/useSuggestionAnnotationCommandConsumer';
import { annotationToFindingData } from './annotationToFindingData';

type SuggestionAnnotationFlyoutProps = {
  instance: WebViewerInstance | null;
  isOpen: boolean;
  virtualReference: VirtualElement | null;
  annotation: ISuggestionAnnotation | null;
  onClose: () => void;
};

export const SuggestionAnnotationFlyout = ({
  instance,
  isOpen,
  virtualReference,
  annotation,
  onClose,
}: SuggestionAnnotationFlyoutProps) => {
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null,
  );
  const dispatch = useDispatch();

  const triggerDocumentReleaseFocus = useCallback(() => {
    releaseDocumentFocus(instance?.Core.documentViewer ?? null);
  }, [instance?.Core.documentViewer]);

  useSuggestionAnnotationCommandConsumer(
    SUGGESTION_ANNOTATION_COMMAND_TYPES.RELEASE_DOCUMENT_FOCUS,
    triggerDocumentReleaseFocus,
  );

  const zIndex = useTheme().zIndices.popover;
  const { styles, attributes } = usePopper(virtualReference, popperElement, {
    placement: 'bottom',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 4],
        },
      },
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['top', 'bottom', 'right', 'left'],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          padding: 10,
        },
      },
    ],
  });

  const finding = useMemo(
    () => (annotation ? annotationToFindingData(annotation) : null),
    [annotation],
  );

  const partBotResult = useMemo(() => {
    const metadata = annotation?.rawAnnotation?.metadata;
    if (metadata?.type === ANNOTATION_METADATA_TYPES.FOUND_IN_FILES) {
      return (metadata as FoundInFileMetadata).partBotResult;
    }
    return null;
  }, [annotation]);

  // Action hooks — each resolves its own availability
  const { createRuleAction, enabled: createRuleEnabled } =
    useCreateRuleAction(partBotResult);
  const { printExtractionUuid, enabled: markInaccurateActionEnabled } =
    useMarkInaccurateAction(partBotResult);
  const { geometricActions, enabled: geometricActionsEnabled } =
    useGeometricActions(annotation);
  const { partDescriptorActions, enabled: partDescriptorActionsEnabled } =
    usePartDescriptorActions(annotation);
  const copy = useCopyAction(finding);

  const annotationId = annotation?.Id;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popperElement && !popperElement.contains(event.target as Node)) {
        onClose();
        if (annotationId) {
          dispatch(
            suggestionAnnotations.queueCommands({
              commands: [
                {
                  type: SUGGESTION_ANNOTATION_COMMAND_TYPES.UNFOCUS,
                  annotationIds: [annotationId],
                },
              ],
            }),
          );
        }
      }
      triggerDocumentReleaseFocus();
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [
    isOpen,
    onClose,
    popperElement,
    annotationId,
    dispatch,
    triggerDocumentReleaseFocus,
  ]);

  if (!isOpen || !virtualReference || !annotation || !finding) {
    return null;
  }

  return (
    <Portal>
      <Box
        ref={setPopperElement}
        style={styles.popper}
        {...attributes.popper}
        zIndex={zIndex}
        bg="page.surface"
        borderRadius="lg"
        boxShadow="popover"
        minWidth={24}
        maxWidth={50}
        border="1px solid"
        borderColor="separator.border"
      >
        <FindingContent
          finding={finding}
          asPopover={false}
          showProvenance={false}
          headerActions={
            <IconButtonGroup ml="auto" flexWrap="nowrap" display="flex">
              {copy.enabled && (
                <CopyAction
                  label={copy.label}
                  tooltipPlacement={copy.tooltipPlacement}
                />
              )}
              {createRuleEnabled && (
                <CreateRuleIconAction
                  createRuleAction={createRuleAction}
                  onClose={onClose}
                />
              )}
            </IconButtonGroup>
          }
          actionSlot={
            <>
              {geometricActionsEnabled && (
                <GeometricActionsSet geometricActions={geometricActions} />
              )}
              {partDescriptorActionsEnabled && (
                <PartDescriptorActionsSet
                  partDescriptorActions={partDescriptorActions}
                />
              )}
            </>
          }
          footerSlot={
            <FindingFooterActions
              as={Box}
              // NB: This override is necessary to match the border radius of the flyout, but it's at odds with our default styling for the footer actions.
              // We should re-examine this inconsistency in the future.
              borderBottomRadius="lg"
            >
              {markInaccurateActionEnabled && (
                <MarkInaccurateAction
                  printExtractionUuid={printExtractionUuid}
                  onClose={onClose}
                />
              )}
              <Flex minWidth={1} grow={1} />
              {learnMoreUrl && <LearnMoreAction url={learnMoreUrl} />}
            </FindingFooterActions>
          }
        />
      </Box>
    </Portal>
  );
};
